import {
  ConciliatesRequest,
  InstallmentType,
  TransactionType,
} from '@/core/domain/financial/BankConciliation/BankConciliation.types';
import {
  Card,
  Col,
  Modal,
  ModalProps,
  Row,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ArrowRightOutlined,
  BankOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { useBankConciliationConciliate } from '@/core/domain/financial/BankConciliation/BankConciliation.hooks';
import { useBankConciliationContext } from '@/pages/FinancialPages/BankConciliationPage';
import { FaUnlink } from 'react-icons/fa';
import formatterPrice from '@/utils/number/formatterPrice';

const { Text, Paragraph } = Typography;

interface Props extends Omit<ModalProps, 'onCancel, onOk'> {
  onOk?: () => void;
  onCancel?: () => void;
}

export default function BankConciliationConfirmModal({ ...props }: Props) {
  const {
    setTransactionType,
    conciliatesData,
    setConciliatesData,
    installmentsIdsSelected,
  } = useBankConciliationContext();
  const conciliate = useBankConciliationConciliate();

  const handleOK = async () => {
    const obj: ConciliatesRequest = {
      conciliates:
        conciliatesData?.map((conciliate) => ({
          ...conciliate,
          installments: conciliate.installments.map((installment) => ({
            installmentId: installment.installmentId,
            type: installment.type,
          })),
        })) ?? [],
    };

    await conciliate.mutateAsync(obj);

    setConciliatesData([]);
    setTransactionType(TransactionType.credit);
    props.onOk?.();
    props.onCancel?.();
  };

  const handlerRemoveInstallment = (installmentId: number) => {
    setConciliatesData((prev) => {
      if (!prev) return [];

      return prev
        .map((transaction) => {
          const hasInstallment = transaction.installments.some(
            (installment) => installment.installmentId === installmentId,
          );

          if (hasInstallment) {
            return {
              ...transaction,
              installments: transaction.installments.filter(
                (i) => i.installmentId !== installmentId,
              ),
            };
          }
          return transaction;
        })
        .filter((transaction) => transaction.installments.length > 0);
    });
  };

  return (
    <Modal
      {...props}
      width={1200}
      title="Resumo da Conciliação Bancária"
      okText="Conciliar"
      onOk={handleOK}
      okButtonProps={{
        disabled: !installmentsIdsSelected?.length,
        loading: conciliate.isLoading,
      }}
    >
      <Paragraph ellipsis>
        Deseja realmente conciliar os lançamentos, eles serão liquidados e
        gerados lançamentos bancários
      </Paragraph>

      <div className="scroll" style={{ maxHeight: 600, overflow: 'auto' }}>
        {conciliatesData?.map((transaction) => {
          return transaction.installments.map((installment) => {
            const isReceivable =
              installment.type === InstallmentType.accountReceivable;

            return (
              <Card
                key={`${transaction.transactionId}-${installment.installmentId}`}
                bodyStyle={{ padding: 12 }}
                style={{ marginBottom: 12 }}
              >
                <Row gutter={24} align="middle">
                  <Col xs={24} md={10}>
                    <Card
                      type="inner"
                      title={
                        <>
                          <BankOutlined /> Transação do Banco
                        </>
                      }
                      style={{ borderRadius: 8, height: 120 }}
                      bodyStyle={{ padding: '6px 12px' }}
                      extra={
                        <>
                          <Text strong>Data:</Text>{' '}
                          {moment(transaction?.payDate).format('DD/MM/YYYY')}
                        </>
                      }
                    >
                      <Paragraph ellipsis style={{ margin: '4px 0' }}>
                        <Text strong>Descrição:</Text> {transaction?.name}
                      </Paragraph>

                      <Paragraph ellipsis>
                        <Text strong>Valor:</Text>{' '}
                        {formatterPrice(transaction?.value || 0)}
                      </Paragraph>
                    </Card>
                  </Col>

                  <Col xs={24} md={1} style={{ textAlign: 'center' }}>
                    <ArrowRightOutlined style={{ fontSize: 24 }} />
                  </Col>

                  <Col xs={24} md={10}>
                    <Card
                      type="inner"
                      title={
                        <>
                          <DollarOutlined />{' '}
                          {isReceivable ? 'Conta a Receber' : 'Conta a Pagar'}
                        </>
                      }
                      style={{ borderRadius: 8, height: 120 }}
                      bodyStyle={{ padding: '6px 12px' }}
                      extra={
                        <Tag color={isReceivable ? 'green' : 'volcano'}>
                          {isReceivable ? 'Recebível' : 'Pagável'}
                        </Tag>
                      }
                    >
                      <Paragraph ellipsis style={{ margin: '4px 0' }}>
                        <Text strong>Código:</Text> {installment.codeIdentifier}
                      </Paragraph>
                      <Paragraph ellipsis>
                        <Text strong>Valor:</Text>{' '}
                        {formatterPrice(installment?.installmentValue || 0)}
                      </Paragraph>
                    </Card>
                  </Col>

                  <Col xs={24} md={3} style={{ textAlign: 'center' }}>
                    <Tooltip
                      trigger={['click', 'hover']}
                      title="Remover Vinculo"
                    >
                      <FaUnlink
                        size={16}
                        cursor="pointer"
                        onClick={() =>
                          handlerRemoveInstallment(installment.installmentId)
                        }
                      />
                    </Tooltip>
                  </Col>
                </Row>
              </Card>
            );
          });
        })}

        {!conciliatesData?.length && (
          <Typography.Text type="secondary">
            Nenhuma conciliação realizada.
          </Typography.Text>
        )}
      </div>
    </Modal>
  );
}
